import { Popover, PopoverContent, PopoverTrigger } from '@/components/UI/Popover';
import { toast } from '@/components/UI/Toast';
import type { NotificationModel } from '@/http/Notifications/Models/NotificationModel';
import { useMarkNotificationAsReadMutation } from '@/pages/NewUI/Dashboard/hooks/useMarkAsReadMutation';
import { cn } from '@/util/cn';
import { NotificationEntityTypes } from '@/util/enums/NotificationEntityTypes';
import NotificationSeverity from '@/util/enums/NotificationSeverity';
import { useGetNotifications } from '@/util/hooks/queries/useNotifications';
import useCurrentUserId from '@/util/hooks/useCurrentUserId';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Handshake,
  Info,
  MoreVertical,
  Network,
  Settings,
  UserCheck,
  X
} from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import NotificationBell from './NotificationBell';

// ---------------------------------------------------------------------------
// Priority types derived from the existing backend severity enum
// ---------------------------------------------------------------------------
type NotificationPriority = 'critical' | 'action-required' | 'informational';

function severityToPriority(severity: NotificationSeverity): NotificationPriority {
  switch (severity) {
    case NotificationSeverity.Error:
      return 'critical';
    case NotificationSeverity.Warning:
      return 'action-required';
    case NotificationSeverity.Success:
    case NotificationSeverity.Information:
    default:
      return 'informational';
  }
}

// ---------------------------------------------------------------------------
// Contextual title mapping – replaces the repeated "Onboarding" label
// ---------------------------------------------------------------------------
function deriveTitle(n: NotificationModel): string {
  // If buttonTitle gives a strong hint, derive from that
  const btn = (n.buttonTitle ?? '').toLowerCase();
  const msg = (n.message ?? '').toLowerCase();

  if (btn.includes('allocate erp') || msg.includes('erp')) {
    return 'ERP Allocation Required';
  }
  if (btn.includes('review') || msg.includes('review')) {
    return 'Review Request';
  }
  if (msg.includes('submitted') || msg.includes('new onboarding request')) {
    return 'Onboarding Request Submitted';
  }
  if (msg.includes('approved')) {
    return 'Request Approved';
  }
  if (msg.includes('rejected') || msg.includes('declined')) {
    return 'Request Declined';
  }
  if (msg.includes('complete') || msg.includes('completed')) {
    return 'Onboarding Complete';
  }
  if (
    msg.includes('document') ||
    n.notificationEntityId === NotificationEntityTypes.Documents
  ) {
    return 'Document Update';
  }
  if (n.notificationEntityId === NotificationEntityTypes.Netconn) {
    return 'Network Connection';
  }
  if (n.notificationEntityId === NotificationEntityTypes.ProfileStatus) {
    return 'Profile Status Update';
  }
  if (n.notificationEntityId === NotificationEntityTypes.Message) {
    return 'New Message';
  }

  // Fallback – use the entryName as-is if it's not the generic "Onboarding"
  if (n.entryName && n.entryName !== 'Onboarding') {
    return n.entryName;
  }
  return 'Onboarding Update';
}

// ---------------------------------------------------------------------------
// Category icon based on entity type
// ---------------------------------------------------------------------------
function CategoryIcon({
  entityType,
  className
}: {
  entityType: NotificationEntityTypes;
  className?: string;
}) {
  const cls = cn('h-3.5 w-3.5', className);
  switch (entityType) {
    case NotificationEntityTypes.Onboarding:
      return <Handshake className={cls} />;
    case NotificationEntityTypes.Documents:
      return <FileText className={cls} />;
    case NotificationEntityTypes.Netconn:
      return <Network className={cls} />;
    case NotificationEntityTypes.Message:
      return <ClipboardCheck className={cls} />;
    case NotificationEntityTypes.ProfileStatus:
      return <UserCheck className={cls} />;
    default:
      return <Bell className={cls} />;
  }
}

// ---------------------------------------------------------------------------
// Short CTA labels
// ---------------------------------------------------------------------------
function normalizeCta(buttonTitle?: string): string {
  if (!buttonTitle) return 'View';
  const t = buttonTitle.toLowerCase().trim();
  if (t.includes('allocate erp number')) return 'Allocate ERP';
  if (t === 'review') return 'Review Request';
  if (t.includes('view')) return 'View Details';
  return buttonTitle;
}

// ---------------------------------------------------------------------------
// Relative timestamp formatting
// ---------------------------------------------------------------------------
function formatRelativeTime(date: Date | string | undefined): string {
  if (!date) return '';
  const now = new Date();
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay === 1) return 'Yesterday';

  // Format as "Jan 12"
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Priority border / styling helpers
// ---------------------------------------------------------------------------
const priorityBorder: Record<NotificationPriority, string> = {
  critical: 'border-l-[3px] border-l-red-500',
  'action-required': 'border-l-[3px] border-l-blue-500',
  informational: 'border-l-[3px] border-l-transparent'
};

const _priorityIcon: Record<
  NotificationPriority,
  typeof AlertTriangle | typeof Info | typeof CheckCircle2
> = {
  critical: AlertTriangle,
  'action-required': Info,
  informational: CheckCircle2
};

// ---------------------------------------------------------------------------
// Notification Row
// ---------------------------------------------------------------------------
interface NotificationRowProps {
  notification: NotificationModel;
  onMarkAsRead: (n: NotificationModel) => void;
  onNavigate: (url: string) => void;
  isRemoving: boolean;
}

const NotificationRow: FC<NotificationRowProps> = ({
  notification,
  onMarkAsRead,
  onNavigate,
  isRemoving
}) => {
  const priority = severityToPriority(notification.notificationSeverityId);
  const title = deriveTitle(notification);
  const ctaLabel = normalizeCta(notification.buttonTitle);
  const isUnread = !notification.isRead;

  const handleRowClick = () => {
    if (notification.redirectUrl) {
      onNavigate(notification.redirectUrl);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowClick();
        }
      }}
      className={cn(
        'group relative flex cursor-pointer gap-3 px-4 py-3 transition-all duration-200',
        priorityBorder[priority],
        isUnread ? 'bg-blue-50/60 hover:bg-blue-50' : 'bg-white hover:bg-gray-50',
        isRemoving && 'pointer-events-none -translate-x-4 opacity-0'
      )}
    >
      {/* Status dot */}
      <div className="flex flex-shrink-0 items-start pt-1">
        <span
          className={cn(
            'mt-0.5 block h-2.5 w-2.5 rounded-full',
            isUnread ? 'bg-blue-500' : 'border-[1.5px] border-gray-300 bg-transparent'
          )}
        />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {/* Title row */}
        <div className="flex items-center gap-1.5">
          <CategoryIcon
            entityType={notification.notificationEntityId}
            className={cn(
              'flex-shrink-0',
              priority === 'critical'
                ? 'text-red-500'
                : priority === 'action-required'
                  ? 'text-blue-500'
                  : 'text-gray-400'
            )}
          />
          <span
            className={cn(
              'truncate text-[13px] leading-tight',
              isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700',
              priority === 'critical' && 'font-bold'
            )}
          >
            {title}
          </span>
        </div>

        {/* Description */}
        <p className="mt-0.5 truncate text-[12.5px] leading-snug text-gray-500">
          {notification.message}
        </p>

        {/* Timestamp + CTA */}
        <div className="mt-1.5 flex items-center gap-3">
          <span className="text-[11px] text-gray-400">
            {formatRelativeTime(notification.dateCreated)}
          </span>
          {notification.redirectUrl && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(notification.redirectUrl!);
              }}
              className={cn(
                'inline-flex items-center gap-0.5 rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors',
                priority === 'critical'
                  ? 'bg-red-50 text-red-600 hover:bg-red-100'
                  : priority === 'action-required'
                    ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {ctaLabel}
              <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Dismiss button */}
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={(e) => {
          e.stopPropagation();
          onMarkAsRead(notification);
        }}
        className={cn(
          'absolute top-2 right-2 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full',
          'border border-gray-200 bg-white text-gray-400 opacity-0 shadow-sm transition-all duration-150',
          'group-hover:opacity-100 hover:border-red-200 hover:text-red-500'
        )}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overflow menu
// ---------------------------------------------------------------------------
interface OverflowMenuProps {
  onMarkAllRead: () => void;
  onClearInformational: () => void;
}

const OverflowMenu: FC<OverflowMenuProps> = ({ onMarkAllRead, onClearInformational }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        aria-label="Notification options"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-52 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-50"
            onClick={() => {
              onMarkAllRead();
              setOpen(false);
            }}
          >
            <CheckCircle2 className="h-3.5 w-3.5 text-gray-400" />
            Mark all as read
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-50"
            onClick={() => {
              onClearInformational();
              setOpen(false);
            }}
          >
            <X className="h-3.5 w-3.5 text-gray-400" />
            Clear informational
          </button>
          <div className="mx-2 border-t border-gray-100" />
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-50"
            onClick={() => setOpen(false)}
          >
            <Settings className="h-3.5 w-3.5 text-gray-400" />
            Notification settings
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const NotificationsDropdown: FC = () => {
  const navigate = useNavigate();
  const accountId = useCurrentUserId() ?? '';
  const { mutate: markNotificationAsRead } = useMarkNotificationAsReadMutation();
  const { data: unreadNotifications, refetch } = useGetNotifications(accountId, false);
  const [removingIds, setRemovingIds] = useState<Set<number>>(new Set());
  const [isOpen, setIsOpen] = useState(false);

  const notifications = useMemo(() => unreadNotifications ?? [], [unreadNotifications]);
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications]
  );

  const clearRemovingId = useCallback((id: number) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const onMarkAsReadSuccess = useCallback(
    (n: NotificationModel) => {
      setTimeout(() => {
        refetch();
        clearRemovingId(n.id);
      }, 250);
    },
    [refetch, clearRemovingId]
  );

  const onMarkAsReadError = useCallback(
    (n: NotificationModel) => {
      toast({ title: 'Failed to dismiss notification', color: 'danger' });
      clearRemovingId(n.id);
    },
    [clearRemovingId]
  );

  const handleMarkAsRead = useCallback(
    (n: NotificationModel) => {
      setRemovingIds((prev) => new Set(prev).add(n.id));
      markNotificationAsRead(
        { body: { id: n.id, isRead: true } },
        {
          onSuccess: () => onMarkAsReadSuccess(n),
          onError: () => onMarkAsReadError(n)
        }
      );
    },
    [markNotificationAsRead, onMarkAsReadSuccess, onMarkAsReadError]
  );

  // Mark all as read
  const handleMarkAllRead = useCallback(() => {
    notifications.forEach((n) => {
      if (!n.isRead) {
        markNotificationAsRead(
          { body: { id: n.id, isRead: true } },
          { onSuccess: () => refetch() }
        );
      }
    });
  }, [notifications, markNotificationAsRead, refetch]);

  // Clear informational only
  const handleClearInformational = useCallback(() => {
    notifications
      .filter(
        (n) =>
          severityToPriority(n.notificationSeverityId) === 'informational' && !n.isRead
      )
      .forEach((n) => {
        markNotificationAsRead(
          { body: { id: n.id, isRead: true } },
          { onSuccess: () => refetch() }
        );
      });
  }, [notifications, markNotificationAsRead, refetch]);

  const handleNavigate = useCallback(
    (url: string) => {
      setIsOpen(false);
      navigate(url, { replace: true });
    },
    [navigate]
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <NotificationBell count={unreadCount} />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[390px] overflow-hidden rounded-xl border border-gray-200 p-0 shadow-xl"
      >
        {/* ---- Sticky header ---- */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-semibold text-gray-900">Notifications</h2>
            {unreadCount > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-500 px-1.5 text-[11px] font-semibold text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          <OverflowMenu
            onMarkAllRead={handleMarkAllRead}
            onClearInformational={handleClearInformational}
          />
        </div>

        {/* ---- Notification list ---- */}
        <div className="max-h-[28rem] overflow-y-auto">
          {notifications.length > 0 ? (
            <div className="divide-y divide-gray-100/80">
              {notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onMarkAsRead={handleMarkAsRead}
                  onNavigate={handleNavigate}
                  isRemoving={removingIds.has(n.id)}
                />
              ))}
            </div>
          ) : (
            /* ---- Empty state ---- */
            <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              </div>
              <p className="text-sm font-medium text-gray-900">
                You&apos;re all caught up!
              </p>
              <p className="mt-1 text-xs text-gray-500">No new notifications.</p>
            </div>
          )}
        </div>

        {/* ---- Sticky footer ---- */}
        {notifications.length > 0 && (
          <div className="sticky bottom-0 z-10 flex items-center justify-between border-t border-gray-100 bg-white px-4 py-2.5">
            <button
              type="button"
              className="text-[12.5px] font-medium text-blue-600 transition-colors hover:text-blue-700 hover:underline"
              onClick={() => handleNavigate('/')}
            >
              View all notifications
            </button>
            <button
              type="button"
              className="text-[12.5px] font-medium text-gray-500 transition-colors hover:text-gray-700 hover:underline"
              onClick={handleMarkAllRead}
            >
              Mark all as read
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsDropdown;
